import { useEffect, useId, useRef, useState, useSyncExternalStore } from 'react';
import { voicePerformance } from '@elena/shared';
import type { WaveMode } from './Waveform';

export type ElenaAvatarProps = { mode: WaveMode; size?: 'compact' | 'hero'; label?: string };
const asset = (name: string) => `${import.meta.env.BASE_URL}elena/elena-${name}-v2.png`;

/** v2 fallback when WebGL is missing. Photographic 2D performance: blink frames and audio-reactive mouth, not a 3D viseme rig. */
export function ElenaPhoto({ mode, size = 'hero', label }: ElenaAvatarProps) {
  const root = useRef<HTMLDivElement>(null);
  const eyeClip = useId();
  const mouthClip = useId();
  const performance = useSyncExternalStore(
    voicePerformance.subscribe,
    voicePerformance.getSnapshot,
  );
  const [blink, setBlink] = useState(false);
  const [ready, setReady] = useState({ blink: false, talk: false });
  const [failed, setFailed] = useState(false);
  const [reduced, setReduced] = useState(true);
  const active =
    mode === 'listening' || mode === 'thinking' ? mode : performance.speaking ? 'speaking' : 'idle';

  useEffect(() => {
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReduced(query.matches);
    update();
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    if (reduced || !ready.blink) return;
    let timer: number;
    const schedule = () => {
      timer = window.setTimeout(
        () => {
          if (!document.hidden) setBlink(true);
          timer = window.setTimeout(() => {
            setBlink(false);
            schedule();
          }, 150);
        },
        3200 + Math.random() * 2600,
      );
    };
    schedule();
    return () => {
      window.clearTimeout(timer);
      setBlink(false);
    };
  }, [reduced, ready.blink]);

  useEffect(() => {
    if (reduced || !window.matchMedia('(pointer: fine)').matches) return;
    const node = root.current;
    const look = (event: PointerEvent) => {
      if (!node) return;
      const rect = node.getBoundingClientRect();
      const x = Math.max(-1, Math.min(1, (event.clientX - rect.left - rect.width / 2) / 400));
      const y = Math.max(-1, Math.min(1, (event.clientY - rect.top - rect.height / 2) / 400));
      node.style.setProperty('--look-x', `${x * 1.2}deg`);
      node.style.setProperty('--look-y', `${-y * 1.2}deg`);
    };
    window.addEventListener('pointermove', look, { passive: true });
    return () => {
      window.removeEventListener('pointermove', look);
      node?.style.removeProperty('--look-x');
      node?.style.removeProperty('--look-y');
    };
  }, [reduced]);

  const status =
    label ??
    (active === 'listening'
      ? 'Slušam te'
      : active === 'thinking'
        ? 'Razmišljam…'
        : active === 'speaking'
          ? 'Pričam ti'
          : 'Tu sam za tebe');
  const mouthOpen = !reduced && ready.talk && active === 'speaking' && performance.energy > 0.12;
  return (
    <div
      ref={root}
      className={`elena-avatar is-${active} is-${size}${reduced ? ' is-still' : ''}`}
      role="img"
      aria-label={`Elena — ${status}`}
    >
      <div className="elena-stage" aria-hidden="true">
        <div className="elena-photo-motion">
          {failed ? (
            <div className="elena-photo-fallback">E</div>
          ) : (
            <svg
              className="elena-photo"
              viewBox="0 0 1312 1199"
              preserveAspectRatio="xMidYMid slice"
            >
              <defs>
                <clipPath id={eyeClip}>
                  <ellipse cx="548" cy="400" rx="68" ry="40" />
                  <ellipse cx="724" cy="379" rx="68" ry="40" />
                </clipPath>
                <clipPath id={mouthClip}>
                  <ellipse cx="650" cy="575" rx="108" ry="65" />
                </clipPath>
              </defs>
              <image
                href={asset('portrait')}
                width="1312"
                height="1199"
                preserveAspectRatio="none"
                onError={() => setFailed(true)}
              />
              <g clipPath={`url(#${eyeClip})`} opacity={blink && !reduced ? 1 : 0}>
                <image
                  href={asset('blink')}
                  width="1312"
                  height="1199"
                  preserveAspectRatio="none"
                  onLoad={() => setReady((r) => ({ ...r, blink: true }))}
                />
              </g>
              <g clipPath={`url(#${mouthClip})`} opacity={mouthOpen ? 1 : 0}>
                <image
                  href={asset('talk')}
                  width="1312"
                  height="1199"
                  preserveAspectRatio="none"
                  onLoad={() => setReady((r) => ({ ...r, talk: true }))}
                />
              </g>
            </svg>
          )}
        </div>
        <span className="elena-scan" />
        <span className="elena-particles">
          <i />
          <i />
          <i />
          <i />
          <i />
        </span>
        <span className="elena-orbit" />
        <span className="elena-corner elena-corner-tl" />
        <span className="elena-corner elena-corner-br" />
        <span className="elena-stage-meta">
          ELENA <small>PERSONAL AI</small>
        </span>
      </div>
      <span className="elena-status">
        <i />
        {status}
        <span className="elena-mini-wave" aria-hidden="true">
          <b />
          <b />
          <b />
          <b />
        </span>
      </span>
    </div>
  );
}
